import { userAxios } from '../index'

// Obtener pacientes normales
const getNormalPatients = async (perPage = 10, page = 1) => {
  try {
    const response = await userAxios.get(`users/patients/all/${perPage}?page=${page}`)
    return response.data
  } catch (error) {
    console.error(error)
  }
}

// Obtener pacientes asegurados
const getInsurancePatients = async (perPage = 10, page = 1) => {
  try {
    const response = await userAxios.get(
      `insurance/users/patients/all/${perPage}?page=${page}`
    )
    return response.data
  } catch (error) {
    console.error(error)
  }
}

// Obtener pacientes normales con filtros
const getNormalPatientsWithFilter = async (perPage = 10, page = 1, filters = {}) => {
  try {
    const searchParams = {
      cities_id: filters.cities_id || null,
      create: filters.create || null,
      date_birth: filters.date_birth || null,
      email: filters.email || null,
      gender: filters.gender || null,
      name: filters.name || null,
      phone: filters.phone || null,
      status: filters.status || null,
    }

    const response = await userAxios.post(
      `users/patients/search/${perPage}`,
      searchParams
    )
    return response.data
  } catch (error) {
    console.error(error)
  }
}

// Obtener pacientes asegurados con filtros
const getInsurancePatientsWithFilter = async (perPage = 10, page = 1, filters = {}) => {
  try {
    const searchParams = {
      cities_id: filters.cities_id || null,
      create: filters.create || null,
      date_birth: filters.date_birth || null,
      email: filters.email || null,
      gender: filters.gender || null,
      name: filters.name || null,
      phone: filters.phone || null,
      status: filters.status || null,
    }

    const response = await userAxios.post(
      `insurance/users/patients/search/${perPage}`,
      searchParams
    )
    return response.data
  } catch (error) {
    console.error(error)
  }
}

// Liberar paciente
const releasePatient = async (patientId, patientType) => {
  try {
    const currentDay = new Date().getDate().toString().padStart(2, '0')
    const type = patientType === 'insurance' ? 2 : 1
    const url = `release-patient/app/${currentDay}-pwd-${patientId}-${type}`
    const response = await userAxios.get(url)

    // Verificar que la respuesta tenga success: true
    if (response.data && response.data.success === true) {
      return response.data
    } else {
      // Si no hay success: true, considerarlo como error
      throw new Error('Patient release failed: unsuccessful response from server')
    }
  } catch (error) {
    console.error('Error releasing patient:', error)
    throw error
  }
}

// Buscar pacientes
const searchPatients = async (searchTerm, searchType = 'name', perPage = 10) => {
  const searchParams = {
    cities_id: null,
    create: null,
    date_birth: null,
    email: null,
    gender: null,
    name: null,
    phone: null,
    status: null,
  }

  // Asignar el valor de búsqueda al campo correspondiente
  if (searchType === 'name') {
    searchParams.name = searchTerm
  } else if (searchType === 'phone') {
    searchParams.phone = searchTerm
  } else if (searchType === 'email') {
    searchParams.email = searchTerm
  } else if (searchType === 'date_birth') {
    searchParams.date_birth = searchTerm
  } else if (searchType === 'gender') {
    searchParams.gender = searchTerm
  } else if (searchType === 'status') {
    searchParams.status = searchTerm
  }

  try {
    const normalPerPage = Math.ceil(perPage / 2)
    const insurancePerPage = Math.floor(perPage / 2)

    const [normalPatientsResponse, insurancePatientsResponse] = await Promise.all([
      userAxios.post(`users/patients/search/${normalPerPage}`, searchParams),
      userAxios.post(`insurance/users/patients/search/${insurancePerPage}`, searchParams),
    ])

    const normalPatients = normalPatientsResponse.data.data.map(patient => ({
      ...patient,
      patient_type: 'normal',
      full_name: `${patient.name} ${patient.lastname}`,
      visit_count: patient.histories?.visit_number || 0,
    }))

    const insurancePatients = insurancePatientsResponse.data.data.map(patient => ({
      ...patient,
      patient_type: 'insurance',
      full_name: `${patient.name} ${patient.lastname}`,
      visit_count: patient.histories?.visit_number || 0,
    }))

    const combinedPatients = [...normalPatients, ...insurancePatients].slice(0, perPage)

    return {
      data: combinedPatients,
      total: normalPatientsResponse.data.total + insurancePatientsResponse.data.total,
    }
  } catch (error) {
    console.error('Error searching patients:', error)
    throw error
  }
}

// Obtener todos los pacientes (normales y asegurados) con paginación
const getAllPatients = async (perPage = 10, page = 1) => {
  try {
    const normalPerPage = Math.ceil(perPage / 2)
    const insurancePerPage = Math.floor(perPage / 2)

    const [normalPatientsResponse, insurancePatientsResponse] = await Promise.all([
      getNormalPatients(normalPerPage, page),
      getInsurancePatients(insurancePerPage, page),
    ])

    const normalPatients = normalPatientsResponse.data.map(patient => ({
      ...patient,
      patient_type: 'normal',
      full_name: `${patient.name} ${patient.lastname}`,
      visit_count: patient.histories?.visit_number || 0,
    }))

    const insurancePatients = insurancePatientsResponse.data.map(patient => ({
      ...patient,
      patient_type: 'insurance',
      full_name: `${patient.name} ${patient.lastname}`,
      visit_count: patient.histories?.visit_number || 0,
    }))

    const combinedPatients = [...normalPatients, ...insurancePatients].slice(0, perPage)

    return {
      data: combinedPatients,
      total: normalPatientsResponse.total + insurancePatientsResponse.total,
      current_page: page,
      per_page: perPage,
      last_page: Math.max(
        normalPatientsResponse.last_page,
        insurancePatientsResponse.last_page
      ),
    }
  } catch (error) {
    console.error('Error getting all patients:', error)
    throw error
  }
}

export default {
  getNormalPatients,
  getInsurancePatients,
  getNormalPatientsWithFilter,
  getInsurancePatientsWithFilter,
  releasePatient,
  searchPatients,
  getAllPatients,
}
